import { useEffect, useRef, useState } from 'react';
import { publicJourneyApi } from '../../api/publicJourneyApi';
import { useTravelText } from '../../app/TravelLocaleContext';
import { acquireStoryMediaSlot } from './storyMediaDownloadGate';
import {
  isStoryMediaAuthError as isAuthError,
  isStoryMediaCanceled,
  isStoryMediaRetryable as isRetryable,
  isStoryMediaSessionExpired,
  isStoryMediaUnavailable as isUnavailable,
} from './storyMediaErrors';

const P = 'travel.publicJourney.story';

/**
 * 接近 viewport 才授權取圖；object URL 僅存於元件生命週期，不持久快取。
 * 語系切換不重抓（不依賴 locale）；publication／media／viewerKey 變更會中止並清除。
 */
export default function PublicJourneyStoryMediaImage({
  publicationId,
  mediaId,
  index,
  total,
  viewerKey,
  onRequireAuth,
  onOpen,
}) {
  const { t, tf } = useTravelText();
  const rootRef = useRef(null);
  const objectUrlRef = useRef(null);
  const abortRef = useRef(null);
  const generationRef = useRef(0);
  const onRequireAuthRef = useRef(onRequireAuth);
  onRequireAuthRef.current = onRequireAuth;
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | unavailable | retryable | auth
  const [objectUrl, setObjectUrl] = useState(null);
  const [near, setNear] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    setPhase('idle');
    setNear(false);
  }, [publicationId, mediaId, viewerKey]);

  useEffect(() => () => {
    generationRef.current += 1;
    abortRef.current?.abort();
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver !== 'function') {
      setNear(true);
      return undefined;
    }
    // jsdom 無版面，IO 不會回報；正式瀏覽器仍依接近 viewport 才取圖
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [publicationId, mediaId, viewerKey]);

  useEffect(() => {
    if (!near || !publicationId || !mediaId) return undefined;
    if (phase === 'ready' || phase === 'unavailable' || phase === 'auth') return undefined;

    const generation = generationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    let releaseSlot = null;
    let cancelled = false;

    setPhase('loading');

    (async () => {
      try {
        releaseSlot = await acquireStoryMediaSlot();
        if (cancelled || generation !== generationRef.current) return;
        const blob = await publicJourneyApi.getStoryMedia(publicationId, mediaId, {
          signal: controller.signal,
        });
        if (cancelled || generation !== generationRef.current) return;
        const url = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setObjectUrl(url);
        setPhase('ready');
      } catch (cause) {
        if (cancelled || generation !== generationRef.current) return;
        if (isStoryMediaCanceled(cause) || controller.signal.aborted) {
          return;
        }
        if (isStoryMediaSessionExpired(cause)) {
          // app 已依共用規則切成匿名；不導向登入（避免多張圖重複導航）、不以匿名自動重試
          setPhase('auth');
          return;
        }
        if (isAuthError(cause)) {
          setPhase('auth');
          onRequireAuthRef.current?.();
          return;
        }
        if (isUnavailable(cause)) {
          setPhase('unavailable');
          return;
        }
        if (isRetryable(cause)) {
          setPhase('retryable');
          return;
        }
        setPhase('unavailable');
      } finally {
        releaseSlot?.();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (abortRef.current === controller) abortRef.current = null;
    };
    // phase 刻意不列入：避免 ready 後重跑；語系不列入：切換語系不重抓
    // eslint-disable-next-line react-hooks/exhaustive-deps -- phase 僅作閘門，列入會造成成功後重抓
  }, [near, publicationId, mediaId, viewerKey, retryToken]);

  const alt = tf(`${P}.media.alt`, { index, total });

  return (
    <figure
      ref={rootRef}
      className="journey-story__media"
      data-testid="public-journey-story-media"
      data-media-id={mediaId}
      data-phase={phase}
    >
      <div className="journey-story__media-frame" aria-busy={phase === 'loading'}>
        {phase === 'ready' && objectUrl && onOpen ? (
          <button
            type="button"
            className="journey-story__media-open"
            aria-label={tf(`${P}.viewer.open`, { index, total })}
            data-testid="public-journey-story-media-open"
            onClick={onOpen}
          >
            <img className="journey-story__media-img" src={objectUrl} alt={alt} />
          </button>
        ) : null}
        {phase === 'ready' && objectUrl && !onOpen ? (
          <img className="journey-story__media-img" src={objectUrl} alt={alt} />
        ) : null}
        {phase === 'loading' || phase === 'idle' ? (
          <p className="journey-story__media-status" data-testid="public-journey-story-media-loading">
            {t(`${P}.media.loading`)}
          </p>
        ) : null}
        {phase === 'unavailable' || phase === 'auth' ? (
          <p className="journey-story__media-status" role="status" data-testid="public-journey-story-media-unavailable">
            {t(`${P}.media.unavailable`)}
          </p>
        ) : null}
        {phase === 'retryable' ? (
          <div className="journey-story__media-retry" data-testid="public-journey-story-media-retry">
            <p className="journey-story__media-status" role="status">{t(`${P}.media.retryable`)}</p>
            <button
              type="button"
              className="journey-story__media-retry-btn"
              onClick={() => {
                setPhase('idle');
                setRetryToken((value) => value + 1);
              }}
            >
              {t(`${P}.media.retry`)}
            </button>
          </div>
        ) : null}
      </div>
    </figure>
  );
}
